import { useEffect, useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Library, Send, FileText, Package, Rocket,
  Sun, Moon, LogOut, ArrowLeft, Command,
} from "lucide-react";

export default function OpsLayout() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => localStorage.getItem("cbs_ops_theme") || "dark");

  useEffect(() => {
    localStorage.setItem("cbs_ops_theme", theme);
  }, [theme]);

  const nav = [
    { to: "/ops", label: "Dashboard", icon: LayoutDashboard, exact: true },
    { to: "/ops/content", label: "Content Library", icon: Library },
    { to: "/ops/publishing", label: "Publishing Queue", icon: Send },
    { to: "/admin/documents", label: "Document Control", icon: FileText, external: true },
    { to: "/admin/products", label: "Product Center", icon: Package, external: true },
    { to: "/ops/launch", label: "Launch Center", icon: Rocket },
  ];
  const isActive = (n) => (n.exact ? pathname === n.to : pathname.startsWith(n.to));

  return (
    <div className={theme === "dark" ? "dark" : ""}>
      <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans">
        <aside className="w-64 shrink-0 hidden md:flex flex-col bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800">
          <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-200 dark:border-slate-800">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Command className="w-4 h-4 text-white" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-sm">CBS Operating</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Platform</div>
            </div>
          </div>

          <nav className="flex-1 p-3 space-y-1">
            {nav.map((n) => {
              const Icon = n.icon;
              return (
                <Link key={n.to} to={n.to} data-testid={`ops-nav-${n.label.split(" ")[0].toLowerCase()}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive(n) && !n.external
                      ? "bg-indigo-600 text-white"
                      : "text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
                  }`}>
                  <Icon className="w-4 h-4" /> {n.label}
                  {n.external && <ArrowLeft className="w-3 h-3 ml-auto rotate-[135deg] opacity-40" />}
                </Link>
              );
            })}
          </nav>

          <div className="p-3 border-t border-slate-200 dark:border-slate-800 space-y-1">
            <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="ops-theme-toggle"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </button>
            <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
              <ArrowLeft className="w-4 h-4" /> Classic Admin
            </Link>
            <button onClick={() => { logout(); navigate("/"); }} data-testid="ops-logout"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-rose-500 hover:bg-slate-100 dark:hover:bg-slate-800">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </aside>

        <div className="flex-1 min-w-0 flex flex-col">
          <header className="h-16 shrink-0 flex items-center justify-between px-6 border-b border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/70 backdrop-blur">
            <div className="font-heading font-semibold">Command Center</div>
            <div className="flex items-center gap-3">
              <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className="md:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <span className="text-sm text-slate-400">{user?.email}</span>
            </div>
          </header>
          <main className="flex-1 p-6 lg:p-8 overflow-x-hidden">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
